import { marketingConsentGranted } from '@/lib/marketingConsent';

// OpenAI Ads Measurement Pixel (oaiq) — conversion sink.
//
// index.html initializes and loads the SDK only after marketing consent.
// This sink rechecks consent for every identity and conversion call.
//
// Pixel-only setup (no CAPI). Three conversions, fired at confirmed success
// boundaries via the existing analytics layer (src/utils/mixpanel.ts and
// src/learn/lib/analytics.ts) — never at click/intent:
//   registration_completed   account creation succeeded (all auth methods)
//   first_lesson_completed   custom event, first Learn lesson finished
//   subscription_created     webhook-confirmed paid subscription
//
// Amounts are integer minor units (cents). event_id keeps webhook-poll
// retries and re-visits idempotent, and doubles as the dedupe key if a
// server-side CAPI twin is ever added (same pixel ID + event name + id).

declare global {
  interface Window {
    oaiq?: (...args: unknown[]) => void;
  }
}

/** Conversion reporting must never break the app — swallow everything. */
const oaiq = (...args: unknown[]) => {
  try {
    if (typeof window === 'undefined' || !marketingConsentGranted()) return;
    window.oaiq?.(...args);
  } catch {
    /* ad pixel must never break the app */
  }
};

/** SHA-256 → lowercase hex, per the pixel's user-matching spec. */
const sha256Hex = async (value: string): Promise<string | null> => {
  try {
    if (typeof crypto === 'undefined' || !crypto.subtle) return null;
    const bytes = new TextEncoder().encode(value);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
};

/**
 * Second `init({ user })` call with hashed identifiers for conversion
 * matching. The SDK merges this with the pixelId init from index.html.
 * Fire-and-forget: hashing is async but conversions don't wait on it.
 */
export const oaiqIdentifyUser = (p: { userId?: string | null; email?: string | null }) => {
  void (async () => {
    try {
      const user: Record<string, string> = {};
      if (p.email) {
        const h = await sha256Hex(p.email.trim().toLowerCase());
        if (h) user.email_sha256 = h;
      }
      if (p.userId) {
        const h = await sha256Hex(p.userId);
        if (h) user.external_id_sha256 = h;
      }
      if (Object.keys(user).length) oaiq('init', { user });
    } catch {
      /* ad pixel must never break the app */
    }
  })();
};

/** Signup succeeded (email or OAuth). Fired from trackAuthEvents.signUpCompleted. */
export const oaiqSignupCompleted = (userId: string, email?: string | null) => {
  oaiq(
    'measure',
    'registration_completed',
    { type: 'customer_action', amount: 0, currency: 'USD' },
    { event_id: `signup-${userId}` },
  );
  oaiqIdentifyUser({ userId, email });
};

const FIRST_LESSON_KEY = 'oaiq_first_lesson_completed';

/**
 * First Learn lesson finished — custom event (no standard oaiq event fits).
 * Once per browser via localStorage; a learner switching devices can fire it
 * twice, acceptable for ad optimization.
 */
export const oaiqFirstLessonCompleted = (p: { pathSlug: string; lessonSlug: string }) => {
  try {
    if (localStorage.getItem(FIRST_LESSON_KEY)) return;
    localStorage.setItem(FIRST_LESSON_KEY, `${p.pathSlug}/${p.lessonSlug}`);
  } catch {
    return; // no storage → can't dedupe, skip rather than spam
  }
  oaiq('measure', 'custom', { type: 'custom' }, { custom_event_name: 'first_lesson_completed' });
};

/**
 * Webhook-confirmed paid subscription. Fired from trackCheckoutCompleted so
 * every checkout surface reports through one place. valueUsd is dollars
 * from the verified Stripe receipt; converted to cents here.
 */
export const oaiqSubscriptionCreated = (p: {
  transactionId?: string;
  tier?: string;
  plan?: string;
  valueUsd?: number;
  userId?: string;
}) => {
  if (!p.transactionId || !/^cs_[A-Za-z0-9_]{5,200}$/.test(p.transactionId)) return;
  oaiq(
    'measure',
    'subscription_created',
    {
      type: 'plan_enrollment',
      ...(p.plan ? { plan_id: `${p.tier || 'subscription'}_${p.plan}` } : {}),
      ...(typeof p.valueUsd === 'number' ? { amount: Math.round(p.valueUsd * 100) } : {}),
      currency: 'USD',
    },
    // Same stable key as the GA transaction_id — dedupes poll retries.
    { event_id: p.transactionId },
  );
};
